import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { LifeBuoy, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Support ticket intake dialog (Round 59 · Item 7).
 *
 * Shows the signed-in user's name/email/phone as read-only context, collects
 * a subject + message, and surfaces the backend's email-delivery result
 * honestly: if the proxy rejected the mail we tell the user instead of
 * pretending it went through.
 */
const SupportDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);  // { reference, email_delivered, email_sent_to, reason }

  // Reset state every time the dialog is reopened fresh.
  useEffect(() => {
    if (open) {
      setSubject(''); setMessage(''); setResult(null); setSubmitting(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3) { toast.error('Subject should be at least 3 characters'); return; }
    if (message.trim().length < 10) { toast.error('Message should be at least 10 characters'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/support/tickets`, { subject, message }, { withCredentials: true });
      setResult(data);
      if (data.email_delivered) {
        toast.success(`Ticket ${data.reference} filed`);
      } else {
        // Spec: tell the user if mail was rejected — don't silently swap addresses.
        toast.warning(`Ticket ${data.reference} saved, but email delivery was rejected`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        toast.error(detail || 'Too many tickets filed in the last hour. Please try again later.');
      } else {
        toast.error(detail || 'Failed to submit support ticket');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="support-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-blue-700" /> Support — we usually reply the same day
          </DialogTitle>
          <DialogDescription>
            Describe what you're stuck on and we'll get back to you by email. Your account details are
            attached automatically, so you don't need to re-enter them.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <form onSubmit={submit} className="space-y-3" data-testid="support-form">
            <div className="grid grid-cols-2 gap-3 text-xs text-slate-600 bg-slate-50 rounded-md p-3 border border-slate-200">
              <div><span className="font-semibold text-slate-500">Name</span><br /><span data-testid="support-user-name">{user?.name || '—'}</span></div>
              <div><span className="font-semibold text-slate-500">Email</span><br /><span data-testid="support-user-email">{user?.email || '—'}</span></div>
              <div className="col-span-2"><span className="font-semibold text-slate-500">Phone</span><br /><span data-testid="support-user-phone">{user?.phone || '—'}</span></div>
            </div>
            <div>
              <Label htmlFor="support-subject">Subject *</Label>
              <Input
                id="support-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Bulk upload failing for policies"
                maxLength={200}
                required
                data-testid="support-subject"
              />
            </div>
            <div>
              <Label htmlFor="support-message">Message *</Label>
              <Textarea
                id="support-message"
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what you were trying to do, what happened and anything we should know to help you faster."
                maxLength={4000}
                required
                data-testid="support-message"
              />
              <p className="text-[10px] text-slate-500 mt-1">{message.length}/4000</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} data-testid="support-cancel">Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-blue-800 hover:bg-blue-900 text-white" data-testid="support-submit">
                {submitting ? 'Submitting…' : 'Send to Support'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3" data-testid="support-confirmation">
            {result.email_delivered ? (
              <div className="p-4 rounded-lg border border-green-200 bg-green-50 flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-700 flex-none mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-900">Ticket filed</p>
                  <p className="text-xs text-green-800 mt-1">
                    Reference: <strong data-testid="support-reference">{result.reference}</strong><br />
                    Your request has been emailed to our support team. We'll reply to {user?.email} from the inbox you contacted.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-700 flex-none mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">Ticket saved, email delivery was rejected</p>
                  <p className="text-xs text-amber-800 mt-1">
                    Reference: <strong data-testid="support-reference">{result.reference}</strong><br />
                    Your request is safely stored in our system, but the email to our support inbox was rejected
                    ({result.reason || 'unknown reason'}). Our team can still see this ticket in the Admin panel.
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={onClose} data-testid="support-close">Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SupportDialog;
